/**
 * MemoryChips — brikkene under et svar når assistenten har rørt hukommelsen
 * (AI-21, #193; memory-plan 1f). Én brikke per `memory_changed`:
 *
 *   saved      «Husket: …»  med Angre, som sletter notatet (DELETE med `id`
 *              fra hendelsen)
 *   pending    «Foreslått for foreldrene: …» — en tenåring eller et barn ba om
 *              et familiebredt notat; en forelder godtar det i innstillingene
 *   forgotten  «Glemt: …»
 *   updated    «Oppdatert: …» uten Angre — `remember({ replaces_ref })`; å
 *              slette notatet ville fjernet mer enn endringen (#212)
 *
 * Angre krever notatets `id`, som bare en live-hendelse har. En brikke hydrert
 * fra historikken (#212, `hydrateThread`) viser hva som skjedde da, uten Angre:
 * notatet kan være slettet, endret eller flyttet siden, og «Hva Stell husker»
 * er stedet å rydde etterpå.
 *
 * Ikke i designhandoffen («Not designed yet» i design/PLAN.md), så brikken er
 * omrisset som «Hentet data» (ToolCallTrace) og Angre er en ghost-knapp —
 * eksisterende primitiver og rolletokens (Pattern 8).
 *
 * Angre-statusen leses fra mutasjonsbufferen (`useMutationState`), ikke fra
 * lokal state: strømmeboblen og det ferdige innlegget er to forskjellige
 * React-noder (MessageThread), så lokal state ville glemt at notatet alt var
 * angret i det svaret ble ferdig. Et notat som er angret, vises som «Glemt».
 */
import { useMutationState } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/primitives';
import type { MemoryChipEntry } from './useAssistantStream';
import { DELETE_MEMORY_KEY, useDeleteMemory } from './useMemories';

const CHIP =
  'inline-flex min-h-9 max-w-full items-center rounded-pill border border-secondary-line px-3 py-1.5 text-xs font-bold text-ink-2';

function MemoryChip({ change }: { change: MemoryChipEntry }) {
  const { t } = useTranslation();
  const del = useDeleteMemory();
  const undone =
    useMutationState({
      filters: {
        mutationKey: DELETE_MEMORY_KEY,
        status: 'success',
        predicate: (m) => m.state.variables === change.id,
      },
    }).length > 0;
  const action = change.action === 'saved' && undone ? 'forgotten' : change.action;
  // Bare et lagret notat fra en live-hendelse kan angres (#212).
  const undoId = action === 'saved' ? change.id : undefined;

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex max-w-full flex-wrap items-center gap-1">
        <span data-testid="assistant-memory-chip" data-action={action} className={CHIP}>
          {t(`assistant.memory.chip.${action}`, { text: change.text })}
        </span>
        {undoId !== undefined ? (
          <Button
            variant="ghost"
            size={44}
            data-testid="assistant-memory-undo"
            aria-label={t('assistant.memory.chip.undo_label', { text: change.text })}
            disabled={del.isPending}
            onClick={() => del.mutate(undoId)}
          >
            {t('assistant.memory.chip.undo')}
          </Button>
        ) : null}
      </div>
      {undoId !== undefined && del.isError ? (
        <p role="alert" className="m-0 text-xs text-bad">
          {t('assistant.memory.chip.undo_error')}
        </p>
      ) : null}
    </div>
  );
}

export function MemoryChips({ changes }: { changes: readonly MemoryChipEntry[] }) {
  return (
    <ul data-testid="assistant-memory-chips" className="m-0 flex list-none flex-col items-start gap-1.5 p-0">
      {changes.map((change, i) => (
        // Samme notat kan dukke opp to ganger i én tur (lagret, så glemt).
        <li key={`${change.ref}-${i}`} className="max-w-full">
          <MemoryChip change={change} />
        </li>
      ))}
    </ul>
  );
}
